import ActionMenu from '@/components/common/ActionMenu';
import { useBalanceChangeDateRangeState } from '@/components/common/BalanceChange';
import { MenuButton } from '@/components/common/Button';
import {
    EntityViewActionButton,
    EntityViewContent,
    entityViewInset,
} from '@/components/common/EntityView';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import HistoricalBalanceChange from '@/components/common/HistoricalBalanceChange';
import PartnerSwitcher from '@/components/common/PartnerSwitcher';
import { EyebrowHeader, Title } from '@/components/common/Typography';
import { toDollarsWithCents } from '@/util/currency';
import { FlexProps, MenuItem } from '@chakra-ui/react';
import { isNumber } from '@common/util/types';
import { Settings01 } from '../../common/icons';
import { showGoalDeleteModal } from '../GoalDeleteModal';
import GoalDetailHeader from '../GoalDetailHeader';
import LowerContent from './LowerContent';
import RetirementGoalChartContainer from './RetirementGoalChartContainer';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

export interface RetirementGoalDetailViewProps {
    onClose: VoidFunction;
}

const RetirementGoalDetailView = ({
    onClose,
}: RetirementGoalDetailViewProps) => {
    const { goal } = useRetirementGoalContext();

    return (
        <FadeInBox w="full" display="block">
            <GoalDetailHeader goal={goal} />
            <EntityViewContent pt={0} pb={16} px={entityViewInset}>
                <ControlSection onClose={onClose} mb={10} />
                <BalanceSection mb={10} />
                <RetirementGoalChartContainer mb={10} />
                <LowerContent />
            </EntityViewContent>
        </FadeInBox>
    );
};

export default RetirementGoalDetailView;

interface ControlSectionProps extends FlexProps {
    onClose: VoidFunction;
}

const ControlSection = ({ onClose, ...props }: ControlSectionProps) => {
    const { isSharedGoal, partnerSwitcherType, setPartnerSwitcherType } =
        useRetirementGoalContext();

    return (
        <FlexRow justify="space-between" align="center" mt={8} {...props}>
            <FlexRow>
                {isSharedGoal && (
                    <PartnerSwitcher
                        value={partnerSwitcherType}
                        onChanged={setPartnerSwitcherType}
                    />
                )}
            </FlexRow>
            {partnerSwitcherType === 'MINE' || isSharedGoal ? (
                <RetirementGoalMenuButton onDeleteComplete={onClose} />
            ) : // no menu when viewing partner's Personal goal
            null}
        </FlexRow>
    );
};

interface BalanceSectionProps extends FlexProps {}

const BalanceSection = ({ ...props }: BalanceSectionProps) => {
    const { balance, goalAccounts, possessiveStr } = useRetirementGoalContext();
    const [balanceChangeDateRange, setBalanceChangeDateRange] =
        useBalanceChangeDateRangeState();

    if (!isNumber(balance)) return null;

    return (
        <FlexCol justifyContent="space-between" {...props}>
            <EyebrowHeader fontSize="sm" color="gray.400">
                {`${possessiveStr} Retirement total`}
            </EyebrowHeader>
            <FlexRow alignItems="flex-end">
                <Title lineHeight={1}>{toDollarsWithCents(balance)}</Title>
                <HistoricalBalanceChange
                    dateRange={balanceChangeDateRange}
                    onChangeDateRange={setBalanceChangeDateRange}
                    positiveAccounts={goalAccounts}
                    ml={3}
                />
            </FlexRow>
        </FlexCol>
    );
};

interface RetirementGoalMenuButtonProps {
    onDeleteComplete: VoidFunction;
}

const RetirementGoalMenuButton = ({
    onDeleteComplete,
}: RetirementGoalMenuButtonProps) => {
    const { goal } = useRetirementGoalContext();

    const deleteMenuItem = (
        <MenuItem
            onClick={() => {
                showGoalDeleteModal({ goal, onComplete: onDeleteComplete });
            }}
        >
            Delete
        </MenuItem>
    );

    return (
        <ActionMenu
            menuButton={
                <MenuButton
                    as={EntityViewActionButton}
                    icon={<Settings01 />}
                    label="Settings"
                    onClickEvent="Retirement Goal Settings Menu Button Clicked"
                />
            }
        >
            {deleteMenuItem}
        </ActionMenu>
    );
};
